import { defineComponent as _defineComponent } from 'vue'
import { ref, Ref, onBeforeMount, onUnmounted } from 'vue';
import CGradientColor from './CGradientColor.vue';
import CSolidColor from './CSolidColor.vue';
import CPaletteColor from './CPaletteColor.vue';
import { type FullGradientObject, type FullPaletteObject, type FullColorObject, ColorPickerType, ColorPickerTypeValue, CColorPickerValue } from '@/composables/color';
import CButtonGroup, { CButtonGroupItem } from '@/components/shared/CButtonGroup';

type ColorPickerMode = 'cmyk' | 'rgb';
type GradientMode = 'linear' | 'radial';

interface ColorPickerProps {
    value?: CColorPickerValue;
    angle?: number;
    mode?: ColorPickerMode;
    initialColor?: string;
    allowReset?: boolean;
    resetText?: string;
    hidePalette?: boolean;
    pickerType?: ColorPickerTypeValue;
    gradientMode?: GradientMode;
    showInputMenu?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CColorPicker',
  props: {
    value: null,
    angle: { default: 90 },
    mode: { default: 'rgb' },
    initialColor: { default: '#8C2525' },
    allowReset: { type: Boolean, default: true },
    resetText: { default: 'Reset' },
    hidePalette: { type: Boolean, default: false },
    pickerType: { default: ColorPickerType.SOLID },
    gradientMode: null,
    showInputMenu: { type: Boolean, default: false }
  },
  emits: ["input", "reset"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: CColorPickerValue): void;
    (event: 'reset', value?: CColorPickerValue): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: CColorPickerValue, angle: number, mode: ColorPickerMode, initialColor: string, allowReset: boolean, resetText: string, hidePalette: boolean, pickerType: ColorPickerTypeValue, gradientMode?: GradientMode, showInputMenu: boolean };

const PICKER_TYPES: CButtonGroupItem<ColorPickerTypeValue>[] = [
    {
        label: 'Solid Color',
        value: ColorPickerType.SOLID
    },
    {
        label: 'Gradient',
        value: ColorPickerType.GRADIENT
    },
    {
        label: 'Alternating Colors',
        value: ColorPickerType.PALETTE
    }
];





const currentColorGradient: Ref<FullGradientObject | undefined> = ref();
const currentColorSolid: Ref<FullColorObject | undefined> = ref();
const currentColorPalette: Ref<FullPaletteObject | undefined> = ref();
const pickerType: Ref<ColorPickerTypeValue> = ref(props.pickerType || ColorPickerType.SOLID);
const pickerTypes: Ref<CButtonGroupItem<ColorPickerTypeValue>[]> = ref(PICKER_TYPES.filter(item => {
    if (item.value === ColorPickerType.PALETTE && props.hidePalette) return false;
    return true;
}));

const onUpdateColor = (color: CColorPickerValue) => {
    const colorWithType = { ...color, typeColor: pickerType.value };

    switch (pickerType.value) {
        case ColorPickerType.SOLID:
            currentColorSolid.value = colorWithType as FullColorObject;
            break;
        case ColorPickerType.PALETTE:
            currentColorPalette.value = colorWithType as FullPaletteObject;
            break;
        case ColorPickerType.GRADIENT:
            currentColorGradient.value = colorWithType as FullGradientObject;
            break;
    }

    emit('input', color);
};

const resetAllColors = () => {
    currentColorGradient.value = undefined;
    currentColorSolid.value = undefined;
    currentColorPalette.value = undefined;
};

const onChangePickerType = (type: ColorPickerTypeValue) => {
    resetAllColors();
    pickerType.value = type;
};

onBeforeMount(() => {
    if (!props.value) return;

    resetAllColors();

    // Better type detection based on unique properties
    // Its necessary for old versions compatibility
    if ('colorStops' in props.value) {
        currentColorGradient.value = props.value as FullGradientObject;
        pickerType.value = ColorPickerType.GRADIENT;
    } else if ('colors' in props.value) {
        currentColorPalette.value = props.value as FullPaletteObject;
        pickerType.value = ColorPickerType.PALETTE;
    } else {
        currentColorSolid.value = props.value as FullColorObject;
        pickerType.value = ColorPickerType.SOLID;
    }
});

onUnmounted(() => {
    resetAllColors();
    pickerType.value = ColorPickerType.SOLID;
});

return { __sfc: true,PICKER_TYPES, props, emit, currentColorGradient, currentColorSolid, currentColorPalette, pickerType, pickerTypes, onUpdateColor, resetAllColors, onChangePickerType, CGradientColor, CSolidColor, CPaletteColor, ColorPickerType, CButtonGroup }
}

})